import React, { useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";

import { logout, syncAuthenticatedUser } from "../store/slices/authSlice";
import { setUser } from "../store/slices/profileSlice";

import { getDeviceId } from "../utils/deviceId";
import { api } from "../services/api";

import {
  showGlobalLoader,
  hideGlobalLoader,
} from "../components/loader/GlobalLoader";

const AppInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const { isLoggedIn, user: authUser } = useSelector((state) => state.auth);


  const authInitializedRef = useRef(false);

  useEffect(() => {
    if (authInitializedRef.current) {
      return;
    }

    authInitializedRef.current = true;

    let mounted = true;
    // Allow the in-flight startup profile lookup to adopt an access token
    // rotated by our own refresh request, but never one from a new OTP login.
    let expectedAccessToken = localStorage.getItem("auth_token");

    const initializeApp = async () => {
      showGlobalLoader();
      const initialToken = localStorage.getItem("auth_token");
      const initialRefresh = localStorage.getItem("refresh_token");
      expectedAccessToken = initialToken;
      const sessionIsCurrent = () =>
        mounted && !!expectedAccessToken &&
        localStorage.getItem("auth_token") === expectedAccessToken;

      try {
        if (!initialToken && !initialRefresh) {
          // Do not overwrite a calculator login that completed after startup.
          if (mounted && !localStorage.getItem("auth_token")) dispatch(logout());
          return;
        }

        // If there is no access token, restore through the shared refresh
        // request. For an expired access token, getProfile's interceptor does
        // the one and only refresh; never trigger another refresh in a catch.
        if (!initialToken && initialRefresh) {
          const refreshResponse = await api.refreshToken(initialRefresh);
          if (refreshResponse?.success !== true) return;
          expectedAccessToken = refreshResponse.data?.access_token || null;
          const refreshedUser = refreshResponse.data?.user;
          if (refreshedUser && sessionIsCurrent()) {
            localStorage.setItem("user_data", JSON.stringify(refreshedUser));
            dispatch(syncAuthenticatedUser(refreshedUser));
            return;
          }
        }

        if (!localStorage.getItem("auth_token")) return;
        const response = await api.getProfile();
        if (response?.success === true) {
          const user = response.data?.user || response.data;
          if (user && sessionIsCurrent()) {
            localStorage.setItem("user_data", JSON.stringify(user));
            dispatch(syncAuthenticatedUser(user));
          }
        }
      } catch (error) {
        // Unauthorized refresh is handled by the shared request manager:
        // credentials are cleared and auth_logout is emitted only once.
        if (error.response?.status !== 401) console.warn("[Auth] Session validation failed:", error);
      } finally {
        if (mounted) hideGlobalLoader();
      }
    };
    initializeApp();

    const handleAuthLogout = () => {
      dispatch(logout());
    };

    const handleTokenRefreshed = (event) => {
      if (event.detail?.previousAccessToken === expectedAccessToken && event.detail?.accessToken) {
        expectedAccessToken = event.detail.accessToken;
      }
      if (event.detail?.user && localStorage.getItem("auth_token")) {
        dispatch(syncAuthenticatedUser(event.detail.user));
      }
    };

    window.addEventListener("auth_logout", handleAuthLogout);

    window.addEventListener("auth_token_refreshed", handleTokenRefreshed);

    return () => {
      mounted = false;

      window.removeEventListener("auth_logout", handleAuthLogout);

      window.removeEventListener("auth_token_refreshed", handleTokenRefreshed);
    };
  }, [dispatch]);

  // The profile pages and the navbar should reflect the same login/logout.
  // A calculator modal can log in without navigating or reloading the app.
  useEffect(() => {
    if (isLoggedIn && authUser) {
      dispatch(setUser(authUser));
    } else if (!isLoggedIn) {
      dispatch(setUser(null));
    }
  }, [dispatch, isLoggedIn, authUser]);

  return children;
};

export default AppInitializer;
